<template>
  <el-container class="client-layout" :class="{ 'compact-shell': useCompactHeader }">
    <!-- 1. 顶部通栏 (Header) -->
    <el-header class="header-upper">
      <div class="container header-flex">
        <!-- 左侧：品牌名称 -->
        <div class="header-name">
          <router-link to="/">
            <div class="brand-title">
              林德大宗气<br />
              <span class="brand-subtitle">产品商城</span>
            </div>
          </router-link>
        </div>

        <!-- 右侧：Logo 图片 -->
        <div class="header-logo">
          <router-link to="/">
            <img src="@/assets/images/logo.png" alt="Linde Logo" />
          </router-link>
        </div>
      </div>
    </el-header>

    <!-- 2. 导航栏 (使用 Grid 系统实现响应式) -->
    <div class="main-navbar">
      <div class="container">
        <!-- xs=24 代表手机端占满全屏(垂直排列)，md=24 代表PC端也是通栏 -->
        <el-row type="flex" justify="center" align="middle">
          <el-col :xs="24" :md="24">
            <el-menu
              mode="horizontal"
              theme="dark"
              :default-active="currentMenuKey"
              @select="handleMenuSelect"
              class="custom-nav-menu"
            >
              <el-menu-item index="/">首页</el-menu-item>
              <el-menu-item v-if="!isRegisteredRole" index="/register-option">注册</el-menu-item>
              <el-menu-item v-if="!isRegisteredRole" index="/login">登录</el-menu-item>
              <el-menu-item v-if="isRegisteredRole" index="supply-mode">供应方式</el-menu-item>
              <el-menu-item v-if="isRegisteredRole" index="/customer-center">客户中心</el-menu-item>
              <el-sub-menu v-if="showOrderAndProductsMenus && showHistoryDropdown" index="history-orders-menu" popupClassName="topmenu-submenu-popup">
                <template #title>历史订单</template>
                <el-menu-item index="history-delivery-orders">配送订单</el-menu-item>
                <el-menu-item index="history-pickup-orders">自提订单</el-menu-item>
              </el-sub-menu>
              <el-menu-item v-else-if="showOrderAndProductsMenus" :index="historyDirectTarget">历史订单</el-menu-item>
              <el-menu-item v-if="showOrderAndProductsMenus" index="/customer-center/draft-distribution-plannings">分销计划</el-menu-item>
              <el-menu-item :index="isRegisteredRole ? '/customer-center/messages' : '/login?redirect=%2Fcustomer-center%2Fmessages'">消息</el-menu-item>
              <el-sub-menu v-if="showOrderAndProductsMenus && showProductsDropdown" index="products-menu" popupClassName="topmenu-submenu-popup">
                <template #title>自助下单</template>
                <el-menu-item index="/products">配送</el-menu-item>
                <el-menu-item index="/customer-center/draft-distribution-plannings?create=true">自提</el-menu-item>
              </el-sub-menu>
              <el-menu-item v-else-if="showOrderAndProductsMenus" :index="productsDirectTarget">自助下单</el-menu-item>
              <el-menu-item v-else-if="!isRegisteredRole" index="/products">自助下单</el-menu-item>
              <el-sub-menu v-if="showVehicleMenu" index="vehicle-menu" popupClassName="topmenu-submenu-popup">
                <template #title>车辆信息</template>
                <el-menu-item index="vehicle-query">车辆查询</el-menu-item>
                <el-menu-item index="vehicle-create">车辆注册</el-menu-item>
              </el-sub-menu>
            </el-menu>
          </el-col>
        </el-row>
      </div>
    </div>

    <!-- 3. 主要内容区 -->
    <el-main class="main-content-wrapper">
      <div class="container">
        <!-- 这里的子页面会自动继承宽度，无需修改子页面代码 -->
        <router-view />
      </div>
    </el-main>

    <!-- 4. 底部 Footer -->
    <el-footer class="site-footer">
      <div class="footer-upper">
        <div class="container footer-upper-inner">
          <section class="footer-info-block">
            <h4 class="footer-title">信息</h4>
            <ul class="footer-link-list">
              <li>
                <router-link class="footer-inline-link" to="/privacy-notice">隐私权声明</router-link>
              </li>
            </ul>
          </section>

          <section class="footer-qrcode-block">
            <h4 class="footer-title">关注我们</h4>
            <div class="footer-qrcode-placeholder" aria-label="微信公众号二维码">
              <img src="@/assets/images/qrcode.jpg" alt="微信公众号二维码" />
            </div>
          </section>
        </div>
      </div>

      <div class="footer-lower">
        <div class="container footer-lower-inner">
          <div class="footer-records">
            <a
              class="record-link"
              href="https://beian.mps.gov.cn/#/query/webSearch?code=31011502007472"
              target="_blank"
              rel="noopener noreferrer"
            >
              沪公网安备 31011502007472号
            </a>
            <a
              class="record-link"
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
            >
              沪ICP备15023620号-3
            </a>
          </div>
          <div class="footer-copyright">
            版权所有 © {{ currentYear }} 林德（中国）投资有限公司 保留所有权利
          </div>
        </div>
      </div>
    </el-footer>

    <VehicleInfoModal v-model:open="vehicleModalVisible" :mode="vehicleModalMode" />
  </el-container>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import VehicleInfoModal from '@/components/vehicle/VehicleInfoModal.vue';
import { useUserStore } from '@/stores/user';

// 添加当前年份的计算属性
const currentYear = computed(() => new Date().getFullYear());

type VehicleModalMode = 'query' | 'create';

const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const vehicleModalVisible = ref(false);
const vehicleModalMode = ref<VehicleModalMode>('query');

const useCompactHeader = computed(() => route.path !== '/');

const roleSystemNames = computed<string[]>(() => {
  const roleValues = userStore.userInfo?.Roles ?? userStore.userInfo?.roles ?? []
  return roleValues
    .filter((item): item is string => typeof item === 'string')
    .map((name) => name.trim())
    .filter(Boolean)
})

const hasDistributionCustomerRole = computed(() => roleSystemNames.value.includes('分销客户'))
const hasUseDepartmentRole = computed(() => roleSystemNames.value.includes('使用部门'))
const isRegisteredRole = computed(() => roleSystemNames.value.includes('Registered'))

const showOrderAndProductsMenus = computed(() => {
  return isRegisteredRole.value && (hasDistributionCustomerRole.value || hasUseDepartmentRole.value)
})

const showVehicleMenu = computed(() => {
  return isRegisteredRole.value && hasDistributionCustomerRole.value
})

const showHistoryDropdown = computed(() => {
  return hasDistributionCustomerRole.value && hasUseDepartmentRole.value
})

const historyDirectTarget = computed(() => {
  if (hasDistributionCustomerRole.value && !hasUseDepartmentRole.value) {
    return 'history-pickup-orders'
  }
  return 'history-delivery-orders'
})

const showProductsDropdown = computed(() => {
  return hasDistributionCustomerRole.value && hasUseDepartmentRole.value
})

const productsDirectTarget = computed(() => {
  if (hasDistributionCustomerRole.value && !hasUseDepartmentRole.value) {
    return '/customer-center/draft-distribution-plannings'
  }
  return '/products'
})

// 计算当前菜单高亮 key
const currentMenuKey = computed(() => {
  if (showOrderAndProductsMenus.value && showHistoryDropdown.value && (route.path === '/delivery-orders' || route.path === '/pickup-orders')) {
    return 'history-orders-menu';
  }
  if (showOrderAndProductsMenus.value && !showHistoryDropdown.value) {
    if (route.path === '/delivery-orders') return 'history-delivery-orders'
    if (route.path === '/pickup-orders') return 'history-pickup-orders'
  }
  if (showOrderAndProductsMenus.value && showProductsDropdown.value && route.path === '/products') {
    return 'products-menu'
  }
  if (showOrderAndProductsMenus.value && route.path === '/customer-center/draft-distribution-plannings' && route.query.create === 'true') {
    return '/customer-center/draft-distribution-plannings?create=true'
  }
  if (showOrderAndProductsMenus.value && route.path === '/customer-center/draft-distribution-plannings') {
    return '/customer-center/draft-distribution-plannings'
  }
  if (isRegisteredRole.value && (route.path === '/customer-center/messages' || route.path.startsWith('/customer-center/message-detail/'))) {
    return '/customer-center/messages'
  }
  if (isRegisteredRole.value && route.path.startsWith('/customer-center')) {
    return '/customer-center'
  }
  if (route.path === '/register-contract') {
    return '/register-option';
  }
  return route.path;
});

// 菜单点击跳转
const handleMenuSelect = (index: string) => {
  const key = index
  if (key === 'supply-mode') {
    window.open('https://wechat.lindemobile.cn/ui/supply-mode.aspx', '_blank', 'noopener,noreferrer');
    return;
  }

  if (key === 'history-delivery-orders') {
    router.push('/delivery-orders');
    return;
  }

  if (key === 'history-pickup-orders') {
    router.push('/pickup-orders');
    return;
  }

  if (key === 'vehicle-query' || key === 'vehicle-create') {
    vehicleModalMode.value = key === 'vehicle-query' ? 'query' : 'create';
    vehicleModalVisible.value = true;
    return;
  }

  if (key !== route.path) {
    router.push(key);
  }
};
</script>

<style scoped>
/* --- 基础设置 --- */
.client-layout {
  min-height: 100vh;
  background-color: var(--mall-bg-page);
}

/* 模拟电商标准容器宽度 */
.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
  box-sizing: border-box;
}

/* --- 1. Header 样式微调 --- */
.header-upper {
  background-color: rgb(227, 235, 240);
  padding: 0; /* 去掉默认 padding，自己控制内部高度 */
  height: auto; /* 允许高度自适应 */
  line-height: normal;
  border-bottom: 1px solid var(--mall-border);
}

.header-flex {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 80px; /* 设定一个舒适的高度 */
}

.brand-title {
  font-size: 22px;
  color: var(--mall-brand-deep);
  line-height: 1.2;
  margin: 0;
}

.brand-subtitle {
  color: var(--mall-brand-link); /* 林德蓝 */
  font-size: 16px;
  font-weight: 500;
}

.header-logo img {
  height: 45px;
  width: auto;
  display: block;
}

/* --- 2. 导航栏样式 (覆盖 Ant Design 默认样式以匹配设计图) --- */
.main-navbar {
  background-color: rgb(242, 246, 249);
}

/* 强制覆盖 a-menu 的背景色为透明或继承，因为父级 div 已经有颜色了 */
.custom-nav-menu {
  background-color: transparent !important;
  border-bottom: none !important;
  line-height: 48px; /* 调整行高让文字居中 */
}

:deep(.custom-nav-menu.ant-menu-horizontal > .ant-menu-item),
:deep(.custom-nav-menu.ant-menu-horizontal > .ant-menu-submenu > .ant-menu-submenu-title) {
  height: 50px !important;
  line-height: 50px !important;
  margin-top: 0;
  margin-bottom: 0;
  padding-inline: 18px !important;
}

/* 修改菜单项文字颜色 */
:deep(.ant-menu-item) {
  color: #005591 !important;
  font-size: 16px;
  font-weight: bold;
}

:deep(.ant-menu-submenu-title) {
  color: #005591 !important;
  font-size: 16px;
  font-weight: bold;
}

:deep(.ant-menu-item:hover),
:deep(.ant-menu-item-selected),
:deep(.ant-menu-submenu-title:hover),
:deep(.ant-menu-submenu-selected > .ant-menu-submenu-title) {
  color: #005591 !important;
  background-color: rgba(0, 122, 185, 0.12) !important;
}

:deep(.custom-nav-menu.ant-menu-horizontal > .ant-menu-submenu > .ant-menu-submenu-title) {
  border-radius: 0 !important;
}

:deep(.custom-nav-menu.ant-menu-horizontal > .ant-menu-item) {
  border-radius: 0 !important;
}

:deep(.ant-menu-submenu .ant-menu) {
  background-color: rgb(242, 246, 249) !important;
}

:deep(.ant-menu-submenu .ant-menu-item) {
  color: #00a0e1 !important;
}

:deep(.ant-menu-submenu-popup > .ant-menu) {
  background-color: rgb(242, 246, 249) !important;
  border-radius: 0 !important;
}

:deep(.ant-menu-submenu-popup .ant-menu-item) {
  color: #00a0e1 !important;
  border-radius: 0 !important;
}

:deep(.ant-menu-submenu-popup .ant-menu-item:hover),
:deep(.ant-menu-submenu-popup .ant-menu-item-selected) {
  color: #005591 !important;
  background-color: rgba(0, 122, 185, 0.12) !important;
}

/* --- 3. 内容区样式 --- */
.main-content-wrapper {
  flex: 1;
}

@media (min-width: 769px) {
  .compact-shell .main-navbar {
    height: 42px;
  }

  .compact-shell .header-flex {
    height: 48px;
  }

  .compact-shell .brand-title {
    font-size: 16px;
    line-height: 1.1;
  }

  .compact-shell .brand-subtitle {
    font-size: 12px;
  }

  .compact-shell .header-logo img {
    height: 30px;
  }

  .compact-shell .custom-nav-menu {
    height: 42px;
    line-height: 42px;
  }

  .compact-shell :deep(.custom-nav-menu.el-menu--horizontal > .el-menu-item),
  .compact-shell :deep(.custom-nav-menu.el-menu--horizontal > .el-sub-menu > .el-sub-menu__title) {
    height: 42px !important;
    line-height: 42px !important;
  }

  .compact-shell :deep(.custom-nav-menu .el-menu-item),
  .compact-shell :deep(.custom-nav-menu .el-sub-menu__title) {
    font-size: 14px;
  }
}

/* --- 4. Footer 样式 --- */
.site-footer {
  background-color: rgb(242, 246, 249);
  height: auto;
  min-height: 0;
  padding: 0;
  font-size: 14px;
}

.footer-upper {
  color: #5482b3;
  border-top: 1px solid rgba(0, 85, 145, 0.14);
  border-bottom: 1px solid rgba(0, 85, 145, 0.14);
}

.footer-upper-inner {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 32px;
  padding-top: 18px;
  padding-bottom: 18px;
}

.footer-title {
  color: #5482b3;
  font-size: 20px;
  font-weight: 400;
  line-height: 1;
  margin: 0 0 12px;
}

.footer-info-block,
.footer-qrcode-block {
  min-width: 240px;
}

.footer-link-list {
  margin: 0;
  color: #5482b3;
}

.footer-link-list li {
  line-height: 1.8;
}

.footer-inline-link {
  color: #5482b3;
  text-decoration: none;
}

.footer-inline-link:hover {
  text-decoration: underline;
}

.footer-qrcode-placeholder {
  width: 80px;
  height: 80px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #7ea3c7;
  font-size: 12px;
}

.footer-lower {
  color: #005591;
  font-weight: bold;
}

.footer-lower-inner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  padding-top: 16px;
  padding-bottom: 16px;
}

.footer-records {
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
}

.record-link {
  color: #005591;
  font-weight: bold;
  text-decoration: none;
  font-size: 12px;
}

.record-link:hover {
  text-decoration: underline;
}

.footer-copyright {
  color: #005591;
  font-weight: bold;
  text-align: right;
  font-size: 12px;
}

/* --- 关键：移动端适配逻辑 --- */
@media (max-width: 768px) {
  .header-flex {
    flex-direction: column; /* 手机上 Logo 和文字上下排列 */
    height: auto;
    padding: 15px 0;
    gap: 10px;
  }

  /* 如果需要在手机上把菜单变成垂直堆叠，取消下面注释 */
  /* 
  .custom-nav-menu {
     mode: vertical; 
  } 
  */

  .footer-upper-inner,
  .footer-lower-inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .footer-records {
    gap: 10px;
  }

  .footer-copyright {
    text-align: left;
  }
}
</style>

<style>
.topmenu-submenu-popup > .ant-menu {
  background-color: rgb(242, 246, 249) !important;
  border-radius: 0 !important;
}

.topmenu-submenu-popup .ant-menu-item {
  background-color: rgb(242, 246, 249) !important;
  border-radius: 0 !important;
  color: #00a0e1 !important;
}

.topmenu-submenu-popup .ant-menu-item:hover,
.topmenu-submenu-popup .ant-menu-item-active,
.topmenu-submenu-popup .ant-menu-item-selected {
  background-color: rgba(0, 122, 185, 0.12) !important;
  color: #005591 !important;
}
</style>